import { createContext, useContext, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getFreeTrial, updateFreeTrial } from "../../api/free-trial";
import { getPlans } from "../../api/plans";
import useToaster from "../../components/alert";
import Const from "../../utils/constant";

const PageContext = createContext();

export const PageProvider = ({ children }) => {
  const toaster = useToaster();

  const getFreeTrialQuery = useQuery({ queryKey: ["free-trial"], queryFn: getFreeTrial });
  const getPlansQuery = useQuery({ queryKey: [{ limit: 50 }], queryFn: getPlans });
  const updateFreeTrialQuery = useMutation({ mutationFn: updateFreeTrial });

  const initialValues = useMemo(() => {
    const data = getFreeTrialQuery?.data?.data || {};
    return { enabled: !!data.enabled, days: data.days ?? 7, plan_id: data.plan_id ?? "" };
  }, [getFreeTrialQuery?.data?.data]);

  const planOptions = useMemo(
    () => [
      { label: "First published monthly plan", value: "" },
      ...(getPlansQuery?.data?.data?.data || []).map((plan) => ({
        label: `${plan.plan_title} (${plan.billing_period})`,
        value: plan.id,
      })),
    ],
    [getPlansQuery?.data?.data?.data],
  );

  const handleSubmit = (values) => {
    updateFreeTrialQuery.mutate(
      { enabled: values.enabled, days: Number(values.days), plan_id: values.plan_id || null },
      {
        onSuccess: () => {
          toaster.success(Const.MESSAGES.SUCCESS, Const.MESSAGES.DATA_IS_UPDATED_SUCCESSFULLY);
          getFreeTrialQuery.refetch();
        },
        onError: (error) => {
          toaster.error("Error", error?.response?.data?.error || "Could not save the free trial settings.");
        },
      },
    );
  };

  return (
    <PageContext.Provider value={{ initialValues, planOptions, handleSubmit, getFreeTrialQuery, updateFreeTrialQuery }}>
      {children}
    </PageContext.Provider>
  );
};

export const useFreeTrialProvider = () => useContext(PageContext);
